import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { Upload, X, Copy, Check, Zap, Circle, TerminalSquare, FileImage, AlertTriangle, Link, Music, Video, Clock, ChevronRight, Package, ArrowLeft, RotateCcw, Binary } from "lucide-react";
import SyntaxHighlighter from "react-syntax-highlighter";
import { atomOneDark } from "react-syntax-highlighter/dist/esm/styles/hljs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const SCAN_LOGS = {
  image: ["$ initializing vision model...","$ loading gemma3n:e2b...","$ preprocessing image buffer...","$ detecting UI component tree...","$ extracting color palette...","$ analyzing typography system...","$ mapping layout grid...","$ inferring design tokens...","$ scanning interaction patterns...","$ resolving spacing system...","$ generating master prompt...","$ finalizing bilingual export..."],
  music: ["$ initializing audio model...","$ loading gemma3n:e2b...","$ decoding audio waveform...","$ detecting BPM & time signature...","$ identifying key & scale...","$ mapping chord progression...","$ analyzing instrumentation...","$ profiling production style...","$ extracting vocal characteristics...","$ detecting similar artists...","$ generating master music prompt...","$ finalizing bilingual export..."],
  video: ["$ initializing video model...","$ loading gemma3n:e2b...","$ parsing video frames...","$ analyzing camera motion...","$ extracting color grade...","$ detecting editing rhythm...","$ profiling lighting setup...","$ analyzing audio design...","$ mapping visual style...","$ identifying motion patterns...","$ generating master video prompt...","$ finalizing bilingual export..."],
  apk:   ["$ initializing APK reverse engineering engine...","$ parsing AndroidManifest & permissions...","$ resolving ARSC resource strings...","$ inspecting DEX bytecode & classes...","$ extracting HTTP/HTTPS endpoints & routes...","$ analyzing event DTO models & schemas...","$ detecting geolocation & search pipelines...","$ inspecting local databases & Room entities...","$ fingerprinting third-party SDKs...","$ synthesizing data flow architecture...","$ generating acquisition script & report..."],
  rea:   ["$ initializing REA static analysis engine...","$ inspecting artifact structure & layout...","$ unpacking modules & recovering sources with Wakaru...","$ extracting internal routes, stores & state contracts...","$ detecting frameworks & component tree...","$ synthesizing architecture reconstruction dossier...","$ generating clean-room blueprint & master prompt..."],
};

const TABS = [
  { id: "file",  label: "IMAGE", icon: FileImage, accept: "image/jpeg,image/png,image/webp", hint: "JPEG · PNG · WEBP · max 20MB · o pega con Ctrl+V" },
  { id: "url",   label: "URL",   icon: Link,      accept: null },
  { id: "music", label: "MUSIC", icon: Music,     accept: "audio/mpeg,audio/wav,audio/ogg,audio/flac,audio/m4a,audio/mp4,audio/aac,.mp3,.wav,.ogg,.flac,.m4a,.aac", hint: "MP3 · WAV · OGG · FLAC · M4A · max 100MB" },
  { id: "video", label: "VIDEO", icon: Video,     accept: "video/mp4,video/quicktime,video/webm,.mp4,.mov,.webm", hint: "MP4 · MOV · WebM · max 200MB — or paste YouTube URL" },
  { id: "apk",   label: "APK",   icon: Package,   accept: ".apk,application/vnd.android.package-archive", hint: "Android APK · max 100MB · base.apk recomendado" },
  { id: "rea",   label: "BUNDLE / REA", icon: Binary, accept: ".js,.mjs,.cjs,.bundle,.wasm,.so,.bin,.elf,.exe,.asar", hint: "JS Bundle · Electron · WASM · Binario · max 50MB · Ingeniería inversa con REA" },
];

const TAB_COLORS = { file: "#22d3ee", url: "#a78bfa", music: "#f472b6", video: "#fb923c", apk: "#4ade80", rea: "#c084fc" };

// What each mode actually does. Lives next to the choice it describes instead of
// in a permanent help block nobody reads.
const MODE_BLURB = {
  file:  "Genera el Master Prompt de una captura de interfaz.",
  url:   "Descarga el HTML de una web pública y lo analiza.",
  music: "Extrae BPM, tonalidad, género e instrumentación.",
  video: "Saca un frame representativo y lo lee cinematográficamente.",
  apk:   "Ingeniería inversa de APKs: endpoints, modelos de datos, arquitectura y pipelines de eventos.",
  rea:   "Ingeniería inversa profunda con REA: recupera módulos JS/Electron con Wakaru, analiza binarios/WASM y genera blueprint clean-room.",
};

// Every analysis mode runs on Gemini 2.5 Flash (free tier). The local
// qwen2.5-coder:7b only comes in for RECREATE.
const MODEL_FOR = () => "gemini-2.5-flash";

function typeFromFilename(name = "") {
  const n = name.toLowerCase();
  if (n.endsWith(".apk")) return "apk";
  if ([".js",".mjs",".cjs",".bundle",".wasm",".so",".bin",".elf",".exe",".asar"].some(e => n.endsWith(e))) return "rea";
  if ([".mp3",".wav",".ogg",".flac",".m4a",".aac"].some(e => n.endsWith(e))) return "music";
  if ([".mp4",".mov",".webm"].some(e => n.endsWith(e))) return "video";
  if (n.startsWith("http")) return "url";
  return "file";
}

function TimeAgo({ ts }) {
  const d = new Date(ts);
  const diff = Math.floor((Date.now() - d) / 1000);
  if (diff < 60) return <span>{diff}s ago</span>;
  if (diff < 3600) return <span>{Math.floor(diff/60)}m ago</span>;
  if (diff < 86400) return <span>{Math.floor(diff/3600)}h ago</span>;
  return <span>{d.toLocaleDateString()}</span>;
}

function useScanLogs(active, mode) {
  const [logs, setLogs] = useState([]);
  const intervalRef = useRef(null);
  const indexRef = useRef(0);
  const logList = SCAN_LOGS[mode === "url" ? "image" : mode] || SCAN_LOGS.image;
  useEffect(() => {
    if (active) {
      setLogs([]); indexRef.current = 0;
      intervalRef.current = setInterval(() => {
        if (indexRef.current < logList.length) { setLogs(prev => [...prev, logList[indexRef.current]]); indexRef.current++; }
      }, 900);
    } else { clearInterval(intervalRef.current); }
    return () => clearInterval(intervalRef.current);
  }, [active, mode]);
  return { logs, logList };
}

// Presentational pieces live at module scope: defined inside the component they
// would be a new type on every render, remounting the drawers once per second
// while the elapsed timer ticks.

const HistoryRow = ({ item, i, panel, onSelect }) => {
    const t = typeFromFilename(item.filename);
    const color = TAB_COLORS[t] || (panel === 'bot' ? "#4ade80" : "#71717a");
    const Icon = TABS.find(x => x.id === t)?.icon || (panel === 'bot' ? Package : FileImage);
    const preview = (item.prompt_result || "").replace(/[#*`]/g, "").slice(0, 140).trim();
    const isImagePrompt = item.analysis_type === "image_prompt";
    return (
      <div key={item.id || i}
        onClick={() => onSelect(item, panel)}
        className="flex items-start gap-3 px-4 py-3 border-b border-[#141418] cursor-pointer hover:bg-white/[0.03] transition-colors group"
      >
        <div className="w-8 h-8 flex items-center justify-center border border-[#1c1c1e] rounded flex-shrink-0 mt-0.5" style={{ background: `${color}12` }}>
          <Icon size={14} style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-[12px] text-[#d4d4d8] truncate">{item.filename || "unknown"}</span>
              <span className="font-mono text-[9px] px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: isImagePrompt ? "#8b5cf618" : item.analysis_type === "rea" ? "#c084fc18" : item.analysis_type === "apk" ? "#4ade8018" : "#22d3ee18", color: isImagePrompt ? "#8b5cf6" : item.analysis_type === "rea" ? "#c084fc" : item.analysis_type === "apk" ? "#4ade80" : "#22d3ee" }}>
                {isImagePrompt ? "PROMPT" : item.analysis_type === "rea" ? "REA" : item.analysis_type === "apk" ? "APK" : "UI"}
              </span>
            </div>
            <span className="font-mono text-[10px] text-[#52525b] flex-shrink-0"><TimeAgo ts={item.created_at} /></span>
          </div>
          {item.origin && item.origin !== item.filename && (
            <p className="font-mono text-[10px] text-[#52525b] mt-0.5 truncate" title={item.origin}>
              {item.origin.startsWith('http') ? '🔗 ' : '📁 '}{item.origin.length > 60 ? item.origin.slice(0,60)+'…' : item.origin}
            </p>
          )}
          <p className="font-mono text-[11px] text-[#71717a] mt-1 leading-relaxed line-clamp-2">{preview}…</p>
        </div>
        <ChevronRight size={14} className="text-[#3f3f46] group-hover:text-[#71717a] flex-shrink-0 mt-1 transition-colors" />
      </div>
    );
};

const Drawer = ({ open, onClose, title, color, icon: Icon, count, onRefresh, loading, items, panel, emptyTitle, emptyHint, onSelect }) => {
    if (!open) return null;
    return (
      <div className="fixed inset-0 z-30 bg-black/70 backdrop-blur-sm flex justify-center overflow-auto" onClick={onClose}>
        <div className="w-full max-w-[820px] mt-16 mb-10 mx-4 h-fit bg-[#0b0e14] border border-[#27272a] rounded-xl overflow-hidden shadow-2xl"
             onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#1c1c1e] sticky top-0 bg-[#0b0e14]">
            <div className="flex items-center gap-2">
              <Icon size={14} style={{ color }} />
              <span className="font-mono text-[12px] uppercase tracking-widest" style={{ color }}>{title}</span>
              <span className="font-mono text-[11px] text-[#52525b]">{count} entries</span>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={onRefresh} className="font-mono text-[11px] text-[#71717a] hover:text-[#d4d4d8] px-2 py-1 border border-[#27272a] rounded transition-colors">↺</button>
              <button onClick={onClose} className="font-mono text-[11px] text-[#71717a] hover:text-[#d4d4d8] px-2 py-1 border border-[#27272a] rounded transition-colors">
                <X size={12} />
              </button>
            </div>
          </div>
          <div className="max-h-[65vh] overflow-auto">
            {loading && (
              <div className="flex items-center justify-center h-32">
                <Circle size={14} className="animate-spin" style={{ color }} />
                <span className="ml-2 font-mono text-[12px] text-[#71717a]">loading…</span>
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="flex flex-col items-center justify-center h-48">
                <Icon size={26} className="text-[#27272a] mb-3" />
                <p className="font-mono text-[12px] text-[#52525b]">{emptyTitle}</p>
                <p className="font-mono text-[11px] text-[#3f3f46] mt-1">{emptyHint}</p>
              </div>
            )}
            {!loading && items.map((item, i) => <HistoryRow key={item.id || i} item={item} i={i} panel={panel} onSelect={onSelect} />)}
          </div>
        </div>
      </div>
    );
};


export default function ReverseEngineer() {
  const [mode, setMode] = useState("file");
  const [analysisMode, setAnalysisMode] = useState("ui_reverse"); // "ui_reverse" | "image_prompt"
  const [promptEngine, setPromptEngine] = useState(() => localStorage.getItem("lyai-prompt-engine") || "gemini"); // "local" | "gemini"
  const [file, setFile] = useState(null);
  const [urlInput, setUrlInput] = useState("");
  const [videoUrl, setVideoUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [botHistory, setBotHistory] = useState([]);
  const [showBotPanel, setShowBotPanel] = useState(false);
  const [fromHistory, setFromHistory] = useState(null); // null | 'history' | 'bot'
  // Recreate (agentic): take Master Prompt → generate HTML via local coder → render in iframe
  const [recreateState, setRecreateState] = useState("idle"); // idle | running | done | error
  const [clipResult, setClipResult] = useState(null); // { prompt_fast, prompt_best, elapsed_s } | { error }
  const [recreateHtml, setRecreateHtml] = useState("");
  const [recreateView, setRecreateView] = useState("preview"); // preview | code
  const [recreateError, setRecreateError] = useState("");
  const fileInputRef = useRef(null);
  const autoAnalyzeRef = useRef(false);
  const handleAnalyzeRef = useRef(null);
  const timerRef = useRef(null);
  const abortRef = useRef(null); // cancels the in-flight analysis when the user walks away
  const { logs: scanLogs, logList } = useScanLogs(status === "analyzing", mode);

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const r = await axios.get(`${API}/analyses?source=web`);
      setHistory(r.data || []);
    } catch (e) { console.error("history fetch:", e); }
    setHistoryLoading(false);
  };

  const fetchBotHistory = async () => {
    try {
      const r = await axios.get(`${API}/analyses?source=telegram`);
      setBotHistory(r.data || []);
    } catch (e) { console.error("bot history:", e); }
  };

  // The idle canvas shows recent analyses, so they load once on mount too —
  // not only when the drawer is opened.
  useEffect(() => { fetchHistory(); }, []);

  useEffect(() => {
    if (showHistory) fetchHistory();
  }, [showHistory]);

  useEffect(() => {
    if (showBotPanel) fetchBotHistory();
  }, [showBotPanel]);

  useEffect(() => {
    if (status === "analyzing") {
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    } else { clearInterval(timerRef.current); }
    return () => clearInterval(timerRef.current);
  }, [status]);

  const reset = () => { setFile(null); setUrlInput(""); setVideoUrl(""); setStatus("idle"); setResult(""); setErrorMsg(""); setClipResult(null); };
  const switchMode = (m) => { setMode(m); reset(); };
  const startOver = () => {
    // Abort first: otherwise a late response would call setStatus("done") and
    // drag the user back into a result they already walked away from.
    abortRef.current?.abort();
    reset(); setRecreateState("idle"); setRecreateHtml(""); setRecreateError(""); setFromHistory(null);
  };

  const processFile = useCallback((f) => {
    if (!f) return;
    // URL mode also takes an image attachment, and its preview is rendered —
    // without generating one here it painted an <img src={null}>.
    const isImage = mode === "file" || mode === "url";
    if (isImage) {
      const allowed = ["image/jpeg","image/jpg","image/png","image/webp"];
      if (!allowed.includes(f.type)) { setErrorMsg(`Invalid type: ${f.type}`); setStatus("error"); return; }
      const reader = new FileReader();
      reader.onload = (e) => { setFile({ raw: f, preview: e.target.result, name: f.name }); setStatus("idle"); setResult(""); setErrorMsg(""); };
      reader.readAsDataURL(f);
    } else {
      setFile({ raw: f, preview: null, name: f.name }); setStatus("idle"); setResult(""); setErrorMsg("");
    }
  }, [mode]);

  const onDrop = useCallback((e) => { e.preventDefault(); setIsDragging(false); const f = e.dataTransfer.files?.[0]; if (f) processFile(f); }, [processFile]);
  const onFileChange = (e) => { const f = e.target.files?.[0]; if (f) { processFile(f); if (autoAnalyzeRef.current) { autoAnalyzeRef.current = false; setTimeout(() => handleAnalyzeRef.current?.(), 150); } } e.target.value = ""; };

  // Pegar con Ctrl+V. Llama a processFile, el mismo camino que el boton y el
  // arrastrar. Solo actua en los modos que aceptan imagen (IMAGE y URL): en
  // MUSIC, VIDEO o APK una imagen pegada no significa nada, asi que se ignora
  // en vez de secuestrar el portapapeles.
  useEffect(() => {
    const onPaste = (e) => {
      if (mode !== "file" && mode !== "url") return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of Array.from(items)) {
        if (it.kind !== "file" || !it.type.startsWith("image/")) continue;
        const f = it.getAsFile();
        if (!f) continue;
        const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
        processFile(new File([f], `pegado-${Date.now()}.${ext}`, { type: f.type }));
        e.preventDefault();
        return;
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [mode, processFile]);

  const canAnalyze = () => {
    if (mode === "file") return !!file?.raw;
    if (mode === "url") return urlInput.trim().length > 0 || !!file?.raw;
    if (mode === "music") return !!file?.raw;
    if (mode === "video") return !!file?.raw || videoUrl.trim().length > 0;
    if (mode === "apk") return !!file?.raw;
    if (mode === "rea") return !!file?.raw;
    return false;
  };

  const handleAnalyze = async () => {
    if (!canAnalyze() || status === "analyzing") return;
    setStatus("analyzing"); setResult(""); setErrorMsg(""); setClipResult(null);
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;
    try {
      let response;
      const fd = new FormData();
      if (mode === "file") {
        fd.append("file", file.raw);
        const endpoint = analysisMode === "image_prompt" ? `${API}/analyze-prompt` : `${API}/analyze`;
        // For PROMPT GEN, fire CLIP-Interrogator in parallel — it returns a
        // canonical SD-style prompt that complements the VLM narrative one.
        if (analysisMode === "image_prompt") {
          const fdClip = new FormData();
          fdClip.append("file", file.raw);
          // Don't await yet — start it racing with the VLM call.
          var clipPromise = axios.post(`${API}/analyze-prompt-clip`, fdClip, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000,
            params: { mode: "both" },
            signal,
          }).then(r => r.data).catch(e => ({ error: e.response?.data?.detail || e.message }));
        }
        const reqOpts = { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000, signal };
        if (analysisMode === "image_prompt") reqOpts.params = { engine: promptEngine };
        response = await axios.post(endpoint, fd, reqOpts);
        if (analysisMode === "image_prompt" && clipPromise) {
          // Wait for CLIP up to its own timeout, then attach result
          try {
            const clipData = await clipPromise;
            setClipResult(clipData);
          } catch (_e) { setClipResult({ error: "CLIP service offline" }); }
        }
      } else if (mode === "url") {
        fd.append("url", urlInput.trim());
        if (file?.raw) fd.append("file", file.raw);
        response = await axios.post(`${API}/analyze-url`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000, signal });
      } else if (mode === "music") {
        fd.append("file", file.raw);
        response = await axios.post(`${API}/analyze-music`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000, signal });
      } else if (mode === "video") {
        if (file?.raw) { fd.append("file", file.raw); response = await axios.post(`${API}/analyze-video`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000, signal }); }
        else { fd.append("url", videoUrl.trim()); response = await axios.post(`${API}/analyze-video`, fd, { timeout: 300000, signal }); }
      } else if (mode === "apk") {
        fd.append("file", file.raw);
        response = await axios.post(`${API}/analyze-apk`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000, signal });
      } else if (mode === "rea") {
        fd.append("file", file.raw);
        response = await axios.post(`${API}/analyze-rea`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000, signal });
      }
      setResult(response.data.prompt_result);
      setStatus("done");
      fetchHistory();
    } catch (err) {
      // The user pressed "Nuevo análisis" while this was in flight — they have
      // already moved on, so don't drag them back with an error screen.
      if (axios.isCancel?.(err) || err?.code === "ERR_CANCELED") return;
      setErrorMsg(err.response?.data?.detail || err.message || "Unknown error");
      setStatus("error");
    }
  };
  handleAnalyzeRef.current = handleAnalyze;

  const loadHistoryItem = (item, panel = 'history') => {
    // The stored analysis_type decides the CLIP column and which endpoint
    // RECREATE hits. Without carrying it over, a PROMPT item opens as UI REVERSE.
    setAnalysisMode(item.analysis_type === "image_prompt" ? "image_prompt" : "ui_reverse");
    if (item.analysis_type === "rea") setMode("rea");
    else if (item.analysis_type === "apk") setMode("apk");
    else setMode(typeFromFilename(item.filename));
    // Drop whatever the previous run left behind.
    setFile(null);
    setClipResult(null);
    setRecreateState("idle");
    setRecreateHtml("");
    setRecreateError("");
    setErrorMsg("");
    setResult(item.prompt_result);
    setStatus("done");
    setShowHistory(false);
    setShowBotPanel(false);
    setFromHistory(panel);
    const t = typeFromFilename(item.filename);
    setMode(t);
  };

  const handleCopy = () => { navigator.clipboard.writeText(result); setCopied(true); setTimeout(() => setCopied(false), 2000); };

  const handleRecreate = async () => {
    if (!result) return;
    setRecreateState("running");
    setRecreateHtml("");
    setRecreateError("");
    setRecreateView("preview");
    try {
      // Rebuilds the layout as HTML on the local coder model. There is no image
      // branch: generating pictures needs a paid API or a local Stable Diffusion,
      // and this box has neither — so PROMPT GEN doesn't offer this button.
      const r = await axios.post(`${API}/recreate`, { prompt: result }, { timeout: 1200000 });
      setRecreateHtml(r.data?.code || "");
      setRecreateState("done");
    } catch (e) {
      setRecreateError(e?.response?.data?.detail || e?.message || "Recreate failed");
      setRecreateState("error");
    }
  };

  const isAnalyzing = status === "analyzing";
  const isDone = status === "done";
  const isError = status === "error";
  const activeColor = TAB_COLORS[mode];
  const hasResult = isDone && !!result;
  // The compose canvas owns the screen until there is something to work on.
  const inWorkspace = isAnalyzing || isError || hasResult || recreateState !== "idle";

  // ---- render --------------------------------------------------------------

  return (
    <div className="min-h-screen bg-[#0b0e14] flex flex-col" style={{ fontFamily: "'JetBrains Mono','Fira Code',monospace" }}>

      {/* Header */}
      <header className="flex flex-wrap items-center justify-between gap-y-2 px-4 md:px-6 py-3 border-b border-[#1c1c1e] flex-shrink-0 sticky top-0 z-20 bg-[#0b0e14]">
        <div className="flex items-center gap-3">
          <a href="/"
            className="flex items-center gap-1.5 font-mono text-[11px] text-[#71717a] hover:text-[#e4e4e7] border border-[#27272a] hover:border-[#52525b] rounded px-2.5 py-1.5 transition-colors">
            <ArrowLeft size={12} /> HUB
          </a>
          <div className="flex items-center gap-2 pl-1">
            <TerminalSquare size={16} style={{ color: activeColor }} />
            <span className="font-mono text-[13px] text-[#d4d4d8] uppercase tracking-widest">LyAi Reverse</span>
            <span className="font-mono text-[10px] text-[#52525b] px-1.5 py-0.5 border border-[#27272a] rounded">v2.2</span>
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          {isAnalyzing && (
            <div className="flex items-center gap-1.5 mr-1">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: activeColor }} />
              <span className="font-mono text-[11px]" style={{ color: activeColor }}>{elapsed}s</span>
            </div>
          )}
          <span className="font-mono text-[11px] text-[#52525b] hidden sm:inline">{MODEL_FOR(mode)}</span>
          <button
            onClick={() => { setShowHistory(h => !h); setShowBotPanel(false); }}
            className="flex items-center gap-1.5 font-mono text-[11px] px-2.5 py-1.5 border rounded transition-colors"
            style={showHistory
              ? { borderColor: "#f59e0b", color: "#f59e0b", background: "#f59e0b12" }
              : { borderColor: "#27272a", color: "#71717a" }}
          >
            <Clock size={12} />
            HISTORY
            {history.length > 0 && !showHistory && (
              <span className="ml-0.5 px-1.5 bg-[#27272a] text-[#a1a1aa] rounded text-[10px]">{history.length}</span>
            )}
          </button>
          <button
            onClick={() => { setShowBotPanel(b => !b); setShowHistory(false); }}
            className="flex items-center gap-1.5 font-mono text-[11px] px-2.5 py-1.5 border rounded transition-colors"
            style={showBotPanel
              ? { borderColor: "#4ade80", color: "#4ade80", background: "#4ade8012" }
              : { borderColor: "#27272a", color: "#71717a" }}
          >
            <Package size={12} />
            BOT
            {botHistory.length > 0 && !showBotPanel && (
              <span className="ml-0.5 px-1.5 bg-[#27272a] text-[#a1a1aa] rounded text-[10px]">{botHistory.length}</span>
            )}
          </button>
        </div>
      </header>

      {/* Drawers */}
      <Drawer open={showHistory} onClose={() => setShowHistory(false)} title="analysis.history" color="#f59e0b"
        icon={Clock} count={history.length} onRefresh={fetchHistory} loading={historyLoading} items={history}
        panel="history" emptyTitle="No analyses yet" emptyHint="Run your first reverse and it appears here"
        onSelect={loadHistoryItem} />
      <Drawer open={showBotPanel} onClose={() => setShowBotPanel(false)} title="bot.telegram" color="#4ade80"
        icon={Package} count={botHistory.length} onRefresh={fetchBotHistory} loading={false} items={botHistory}
        panel="bot" emptyTitle="No bot analyses yet" emptyHint="Send an APK to the Telegram bot"
        onSelect={loadHistoryItem} />

      {/* One hidden file input for every mode. */}
      <input ref={fileInputRef} type="file"
        accept={mode === "url" ? "image/jpeg,image/jpg,image/png,image/webp,image/gif" : (TABS.find(t => t.id === mode)?.accept || "*")}
        onChange={onFileChange} className="hidden" />

      <main className="flex-1">
        <div className="mx-auto w-full max-w-[1100px] px-4 md:px-8 py-8 md:py-12">

          {/* ================= COMPOSE ================= */}
          {!inWorkspace && (
            <>
              {/* Mode tabs — centered, legible */}
              <div className="flex flex-wrap justify-center gap-2 mb-8">
                {TABS.map(({ id, label, icon: Icon }) => (
                  <button key={id}
                    onClick={() => { switchMode(id); if (id !== 'url' && id !== 'video') { autoAnalyzeRef.current = true; setTimeout(() => fileInputRef.current?.click(), 50); } }}
                    className="flex items-center gap-2 px-4 py-2.5 font-mono text-[12px] uppercase tracking-widest border rounded-lg transition-all"
                    style={mode === id
                      ? { color: TAB_COLORS[id], borderColor: `${TAB_COLORS[id]}66`, background: `${TAB_COLORS[id]}12` }
                      : { color: '#71717a', borderColor: '#27272a' }}
                  >
                    <Icon size={15} />{label}
                  </button>
                ))}
              </div>

              <p className="text-center font-mono text-[12px] text-[#52525b] mb-8 -mt-4">{MODE_BLURB[mode]}</p>

              <div className="mx-auto max-w-[620px] flex flex-col gap-4">

                {/* Drop zone — the hero of the empty state */}
                {(mode === "file" || mode === "music" || mode === "apk" || mode === "rea" || (mode === "video" && !videoUrl)) && (
                  <div
                    onDrop={onDrop}
                    onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onDragLeave={() => setIsDragging(false)}
                    onClick={() => !file && fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-xl transition-all cursor-pointer min-h-[220px] flex flex-col items-center justify-center relative ${file ? "cursor-default" : ""}`}
                    style={{
                      borderColor: isDragging || file ? activeColor + "80" : "#27272a",
                      background: isDragging ? `${activeColor}0d` : `linear-gradient(180deg, ${activeColor}06, transparent)`,
                    }}
                  >
                    {file ? (
                      <div className="relative w-full p-4">
                        {file.preview
                          ? <img src={file.preview} alt="preview" className="w-full object-contain max-h-[260px] rounded" />
                          : (
                            <div className="flex flex-col items-center gap-2 py-8">
                              {mode === "music" ? <Music size={34} style={{ color: activeColor }} />
                               : mode === "apk" ? <Package size={34} style={{ color: activeColor }} />
                               : mode === "rea" ? <Binary size={34} style={{ color: activeColor }} />
                               : <Video size={34} style={{ color: activeColor }} />}
                              <span className="font-mono text-[12px] text-[#d4d4d8] text-center break-all px-2">{file.name}</span>
                              <span className="font-mono text-[11px] text-[#71717a]">{(file.raw.size / 1024 / 1024).toFixed(1)} MB</span>
                            </div>
                          )
                        }
                        <button onClick={(e) => { e.stopPropagation(); reset(); }}
                          className="absolute top-3 right-3 w-7 h-7 bg-black/80 border border-[#3f3f46] flex items-center justify-center text-[#a1a1aa] hover:text-white rounded transition-colors">
                          <X size={12} />
                        </button>
                        {file.preview && <div className="absolute bottom-3 left-3 font-mono text-[11px] text-[#a1a1aa] bg-black/80 px-2 py-1 rounded">{file.name}</div>}
                      </div>
                    ) : (
                      <>
                        <Upload size={30} className="mb-3" style={{ color: activeColor, opacity: 0.7 }} />
                        <p className="font-mono text-[14px] text-[#d4d4d8]">Arrastra tu {mode === "file" ? "captura" : mode === "music" ? "audio" : mode === "apk" ? "APK" : mode === "rea" ? "bundle o binario" : "vídeo"} aquí</p>
                        <p className="font-mono text-[12px] text-[#52525b] mt-1.5">o pulsa para elegir</p>
                        <p className="font-mono text-[11px] text-[#3f3f46] mt-3">{TABS.find(t => t.id === mode)?.hint}</p>
                      </>
                    )}
                  </div>
                )}

                {/* URL input */}
                {mode === "url" && (
                  <div className="flex flex-col gap-3">
                    <div className="relative">
                      <Link size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#52525b]" />
                      <input type="url" value={urlInput} onChange={e => setUrlInput(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && canAnalyze() && handleAnalyze()}
                        placeholder="https://example.com o https://img.../screen.png"
                        className="w-full bg-[#0f1117] border border-[#27272a] text-[#e4e4e7] font-mono text-[13px] pl-10 pr-3 py-3.5 rounded-lg focus:outline-none focus:border-[#a78bfa]/50 placeholder-[#3f3f46] transition-colors"
                      />
                    </div>
                    <div className="font-mono text-[11px] text-[#52525b] leading-relaxed">
                      <p>• URL de imagen directa → se analiza tal cual</p>
                      <p>• Página web → se extrae el og:image y se analiza</p>
                      <p className="text-[#3f3f46] mt-1.5">Las SPA (React/Vue/Svelte) solo sirven el shell — para esas, mejor captura + pestaña IMAGE.</p>
                    </div>

                    {!file ? (
                      <>
                        <div className="flex items-center gap-3 my-1">
                          <div className="flex-1 h-px bg-[#27272a]" />
                          <span className="font-mono text-[11px] text-[#3f3f46]">O ADJUNTA UNA IMAGEN</span>
                          <div className="flex-1 h-px bg-[#27272a]" />
                        </div>
                        <button onClick={() => fileInputRef.current?.click()}
                          className="w-full py-3 border border-[#27272a] rounded-lg font-mono text-[12px] text-[#71717a] hover:text-[#d4d4d8] hover:border-[#a78bfa]/40 transition-colors flex items-center justify-center gap-2">
                          <Upload size={14} /> Adjuntar imagen o captura
                        </button>
                      </>
                    ) : (
                      <div className="relative border border-[#a78bfa]/30 rounded-lg overflow-hidden">
                        <img src={file.preview} alt={file.name} className="w-full max-h-44 object-cover opacity-80" />
                        <button onClick={() => setFile(null)}
                          className="absolute top-2 right-2 p-1.5 bg-black/80 border border-[#3f3f46] rounded text-[#a1a1aa] hover:text-[#ef4444] transition-colors">
                          <X size={12} />
                        </button>
                        <span className="absolute bottom-2 left-3 font-mono text-[11px] text-[#a78bfa] bg-black/70 px-2 py-0.5 rounded">{file.name}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* VIDEO URL */}
                {mode === "video" && !file && (
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-px bg-[#27272a]" />
                      <span className="font-mono text-[11px] text-[#3f3f46]">O PEGA UNA URL</span>
                      <div className="flex-1 h-px bg-[#27272a]" />
                    </div>
                    <div className="relative">
                      <Video size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#52525b]" />
                      <input type="url" value={videoUrl} onChange={e => setVideoUrl(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && canAnalyze() && handleAnalyze()}
                        placeholder="https://youtube.com/watch?v=..."
                        className="w-full bg-[#0f1117] border border-[#27272a] text-[#e4e4e7] font-mono text-[13px] pl-10 pr-3 py-3.5 rounded-lg focus:outline-none focus:border-[#fb923c]/50 placeholder-[#3f3f46] transition-colors"
                      />
                    </div>
                  </div>
                )}

                {/* Analysis mode — each option carries its own explanation */}
                {mode === "file" && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button onClick={() => setAnalysisMode("ui_reverse")}
                      className="text-left p-3.5 border rounded-lg transition-all"
                      style={analysisMode === "ui_reverse"
                        ? { borderColor: "#22d3ee66", background: "#22d3ee0d" }
                        : { borderColor: "#27272a" }}>
                      <div className="font-mono text-[12px] uppercase tracking-widest mb-1.5"
                        style={{ color: analysisMode === "ui_reverse" ? "#22d3ee" : "#71717a" }}>UI Reverse</div>
                      <p className="font-mono text-[11px] text-[#52525b] leading-relaxed">
                        Master Prompt para recrear la interfaz en v0.dev / Lovable / Claude Artifacts: layout, paleta hex, tipografía, componentes.
                      </p>
                    </button>
                    <button onClick={() => setAnalysisMode("image_prompt")}
                      className="text-left p-3.5 border rounded-lg transition-all"
                      style={analysisMode === "image_prompt"
                        ? { borderColor: "#8b5cf666", background: "#8b5cf60d" }
                        : { borderColor: "#27272a" }}>
                      <div className="font-mono text-[12px] uppercase tracking-widest mb-1.5"
                        style={{ color: analysisMode === "image_prompt" ? "#8b5cf6" : "#71717a" }}>Prompt Gen</div>
                      <p className="font-mono text-[11px] text-[#52525b] leading-relaxed">
                        Un párrafo descriptivo (estilo + sujeto + composición) para Midjourney, DALL-E o Flux. Añade el canónico de CLIP.
                      </p>
                    </button>
                  </div>
                )}

                {/* Engine selector — only on PROMPT GEN */}
                {mode === "file" && analysisMode === "image_prompt" && (
                  <div className="flex gap-3">
                    <button
                      onClick={() => { setPromptEngine("gemini"); localStorage.setItem("lyai-prompt-engine", "gemini"); }}
                      className="flex-1 py-2.5 font-mono text-[11px] uppercase tracking-widest border rounded-lg transition-all"
                      style={promptEngine === "gemini"
                        ? { borderColor: "#10b981", color: "#10b981", background: "#10b98112" }
                        : { borderColor: "#27272a", color: "#71717a" }}
                      title="Gemini 2.5 Flash · cloud · ~5s · free 1500/day"
                    >
                      Gemini · 5s
                    </button>
                    <button
                      onClick={() => { setPromptEngine("local"); localStorage.setItem("lyai-prompt-engine", "local"); }}
                      className="flex-1 py-2.5 font-mono text-[11px] uppercase tracking-widest border rounded-lg transition-all"
                      style={promptEngine === "local"
                        ? { borderColor: "#f59e0b", color: "#f59e0b", background: "#f59e0b12" }
                        : { borderColor: "#27272a", color: "#71717a" }}
                      title="Llama 3.2-vision 11B · local CPU · ~90s · offline"
                    >
                      Local · 90s
                    </button>
                  </div>
                )}

                {/* CTA — full width, accent coloured */}
                <button onClick={handleAnalyze} disabled={!canAnalyze()}
                  className="w-full py-4 font-mono text-[13px] font-bold uppercase tracking-widest flex items-center justify-center gap-2.5 rounded-lg transition-all"
                  style={canAnalyze()
                    ? { background: activeColor, color: "#06131a" }
                    : { background: "#141418", color: '#3f3f46', cursor: 'not-allowed', border: "1px solid #27272a" }}
                >
                  <Zap size={15} /> REVERSE {mode.toUpperCase()}
                </button>

                <p className="text-center font-mono text-[11px] text-[#3f3f46]">
                  Motor: <span style={{ color: activeColor }}>
                    {mode === "file" && analysisMode === "image_prompt"
                      ? (promptEngine === "gemini" ? "Gemini 2.5 Flash · cloud · ~5s" : "Llama 3.2-vision 11B · local · ~90s")
                      : MODEL_FOR(mode)}
                  </span> · salida en EN + ES
                </p>
              </div>

              {/* Recent analyses — the leftover space now earns its keep */}
              {history.length > 0 && (
                <div className="mt-14">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#52525b]">// últimos análisis</span>
                    <button onClick={() => setShowHistory(true)}
                      className="font-mono text-[11px] text-[#52525b] hover:text-[#a1a1aa] transition-colors">ver todos →</button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {history.slice(0, 6).map((item, i) => {
                      const t = typeFromFilename(item.filename);
                      const color = TAB_COLORS[t] || "#71717a";
                      const Icon = TABS.find(x => x.id === t)?.icon || FileImage;
                      const preview = (item.prompt_result || "").replace(/[#*`]/g, "").slice(0, 90).trim();
                      return (
                        <button key={item.id || i} onClick={() => loadHistoryItem(item)}
                          className="text-left p-3.5 border border-[#1c1c1e] rounded-lg hover:border-[#3f3f46] hover:bg-white/[0.02] transition-all group">
                          <div className="flex items-center gap-2.5 mb-2">
                            <div className="w-7 h-7 flex items-center justify-center rounded flex-shrink-0" style={{ background: `${color}12` }}>
                              <Icon size={13} style={{ color }} />
                            </div>
                            <span className="font-mono text-[11px] text-[#a1a1aa] truncate flex-1">{item.filename || "unknown"}</span>
                            <span className="font-mono text-[10px] text-[#3f3f46] flex-shrink-0"><TimeAgo ts={item.created_at} /></span>
                          </div>
                          <p className="font-mono text-[10px] text-[#52525b] leading-relaxed line-clamp-2">{preview}…</p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ================= WORKSPACE ================= */}
          {inWorkspace && (
            <div className="grid md:grid-cols-[220px_1fr] gap-5">

              {/* Input summary */}
              <aside className="flex flex-col gap-3 md:sticky md:top-24 md:self-start">
                <div className="border border-[#1c1c1e] rounded-lg overflow-hidden bg-[#0f1117]">
                  {file?.preview ? (
                    <img src={file.preview} alt={file.name} className="w-full h-28 object-cover" />
                  ) : (
                    <div className="h-20 flex items-center justify-center" style={{ background: `${activeColor}0d` }}>
                      {(() => { const I = TABS.find(t => t.id === mode)?.icon || FileImage; return <I size={24} style={{ color: activeColor }} />; })()}
                    </div>
                  )}
                  <div className="p-2.5">
                    <div className="font-mono text-[11px] text-[#a1a1aa] truncate">{file?.name || urlInput || videoUrl || "desde el historial"}</div>
                    <div className="font-mono text-[10px] text-[#52525b] mt-1">{mode.toUpperCase()}</div>
                  </div>
                </div>

                {/* analysisMode also governs the CLIP column and which endpoint
                    RECREATE hits, so it has to stay reachable after a result. */}
                {mode === "file" && (
                  <div className="grid grid-cols-2 gap-1.5">
                    <button onClick={() => setAnalysisMode("ui_reverse")}
                      className="py-2 font-mono text-[10px] uppercase tracking-widest border rounded transition-all"
                      style={analysisMode === "ui_reverse"
                        ? { borderColor: "#22d3ee66", color: "#22d3ee", background: "#22d3ee0d" }
                        : { borderColor: "#27272a", color: "#52525b" }}>
                      UI
                    </button>
                    <button onClick={() => setAnalysisMode("image_prompt")}
                      className="py-2 font-mono text-[10px] uppercase tracking-widest border rounded transition-all"
                      style={analysisMode === "image_prompt"
                        ? { borderColor: "#8b5cf666", color: "#8b5cf6", background: "#8b5cf60d" }
                        : { borderColor: "#27272a", color: "#52525b" }}>
                      Prompt
                    </button>
                  </div>
                )}

                <button onClick={startOver}
                  className="w-full py-2.5 font-mono text-[11px] uppercase tracking-widest border border-[#27272a] rounded-lg text-[#71717a] hover:text-[#e4e4e7] hover:border-[#52525b] transition-colors flex items-center justify-center gap-2">
                  <RotateCcw size={13} /> Nuevo análisis
                </button>

                <div className="border border-[#1c1c1e] rounded-lg p-3">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#3f3f46] mb-1.5">Motor</div>
                  <div className="font-mono text-[11px]" style={{ color: activeColor }}>{MODEL_FOR(mode)}</div>
                  {isAnalyzing && <div className="font-mono text-[11px] text-[#52525b] mt-1">{elapsed}s transcurridos</div>}
                </div>
              </aside>

              {/* Output */}
              <section className="border border-[#1c1c1e] rounded-lg overflow-hidden flex flex-col min-h-[70vh]">
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#1c1c1e] flex-shrink-0 bg-[#0f1117]">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[11px] text-[#71717a] uppercase tracking-widest">{mode === "apk" || mode === "rea" ? "output.reverse_report" : "output.master_prompt"}</span>
                    {isDone && <span className="font-mono text-[10px] border px-1.5 py-0.5 rounded" style={{ color: '#34d399', borderColor: '#34d39940' }}>COMPLETE</span>}
                    {isAnalyzing && <span className="font-mono text-[10px] border px-1.5 py-0.5 rounded animate-pulse" style={{ color: activeColor, borderColor: activeColor + '40' }}>ANALYZING</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {fromHistory && (
                      <button
                        onClick={() => { fromHistory === 'bot' ? setShowBotPanel(true) : setShowHistory(true); setFromHistory(null); }}
                        className="flex items-center gap-1.5 font-mono text-[11px] px-2 py-1 border border-[#27272a] rounded text-[#71717a] hover:text-[#d4d4d8] transition-colors"
                      >
                        ← {fromHistory === 'bot' ? 'BOT' : 'HISTORY'}
                      </button>
                    )}
                    {isDone && result && recreateState === "idle" && (
                      <>
                        {/* Only for UI REVERSE: RECREATE rebuilds a layout as HTML.
                            A PROMPT GEN result describes a picture, and an APK result is a deep technical report. */}
                        {analysisMode !== "image_prompt" && mode !== "apk" && (
                          <button onClick={handleRecreate}
                            className="flex items-center gap-1.5 font-mono text-[11px] px-2.5 py-1 border border-[#a78bfa]/40 rounded text-[#a78bfa] hover:bg-[#a78bfa]/10 transition-colors"
                            title="Rebuild this layout as HTML with the local coder model">
                            <Zap size={12} /> RECREATE
                          </button>
                        )}
                        <button onClick={handleCopy} className="flex items-center gap-1.5 font-mono text-[11px] px-2.5 py-1 border border-[#27272a] rounded text-[#a1a1aa] hover:text-white transition-colors">
                          {copied ? <Check size={12} className="text-emerald-400" /> : <Copy size={12} />}
                          {copied ? "COPIED" : "COPY"}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex-1 overflow-auto">

                  {isAnalyzing && (
                    <div className="p-6 flex flex-col gap-2">
                      <div className="font-mono text-[12px] mb-2" style={{ color: activeColor }}>{`// INFERENCE IN PROGRESS — ${MODEL_FOR(mode)}`}</div>
                      {logList.map((log, i) => (
                        <div key={i} className="font-mono text-[12px] text-[#71717a]" style={{ opacity: i < scanLogs.length ? 1 : 0.25 }}>
                          <span style={{ color: i < scanLogs.length ? activeColor : '#3f3f46' }}>{i < scanLogs.length ? "✓" : "○"}</span> {log}
                        </div>
                      ))}
                      <div className="mt-4 font-mono text-[12px] text-[#71717a]">
                        [{Array(Math.min(20, Math.floor(scanLogs.length / logList.length * 20))).fill("█").join("")}
                        {Array(Math.max(0, 20 - Math.floor(scanLogs.length / logList.length * 20))).fill("░").join("")}]
                        {" "}{Math.floor(scanLogs.length / logList.length * 100)}%
                      </div>
                    </div>
                  )}

                  {isError && (
                    <div className="h-full flex flex-col items-center justify-center p-6">
                      <div className="border border-[#ef4444]/40 bg-[#ef4444]/5 rounded-lg p-5 max-w-md w-full">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle size={15} className="text-[#ef4444]" />
                          <span className="font-mono text-[12px] text-[#ef4444] uppercase tracking-widest">FAILED</span>
                        </div>
                        <p className="font-mono text-[12px] text-[#a1a1aa] leading-relaxed">{errorMsg}</p>
                        <button onClick={() => { setStatus("idle"); setErrorMsg(""); }} className="mt-4 font-mono text-[12px] text-[#ef4444] border border-[#ef4444]/40 rounded hover:bg-[#ef4444]/10 px-3 py-1.5 transition-colors">RETRY</button>
                      </div>
                    </div>
                  )}

                  {isDone && result && recreateState === "idle" && (
                    <div className="flex flex-col lg:flex-row h-full">
                      {/* VLM narrative */}
                      <div className={analysisMode === "image_prompt" ? "flex-1 border-b lg:border-b-0 lg:border-r border-[#1c1c1e] overflow-auto" : "flex-1 overflow-auto"}>
                        <div className="px-3 py-2 border-b border-[#1c1c1e] bg-[#0a0a0a]">
                          <span className="font-mono text-[10px] uppercase tracking-widest text-[#52525b]">
                            {mode === "apk" ? "// REVERSE RECONSTRUCTION REPORT (APK)" : mode === "rea" ? "// REVERSE RECONSTRUCTION REPORT (REA)" : mode === "music" ? "// AUDIO NARRATIVE" : mode === "video" ? "// VIDEO NARRATIVE" : "// VLM NARRATIVE"}
                          </span>
                        </div>
                        <SyntaxHighlighter language="markdown" style={atomOneDark} showLineNumbers={false} wrapLongLines={true}
                          customStyle={{ background: "#000000", padding: "1.5rem", margin: 0, fontSize: "0.82rem", lineHeight: "1.7", fontFamily: "'JetBrains Mono','Fira Code',monospace" }}>
                          {result}
                        </SyntaxHighlighter>
                      </div>

                      {/* CLIP-Interrogator canonical (only on PROMPT GEN) */}
                      {analysisMode === "image_prompt" && (
                        <div className="flex-1 overflow-auto bg-[#050505]">
                          <div className="px-3 py-2 border-b border-[#1c1c1e] flex items-center justify-between bg-[#0a0a0a]">
                            <span className="font-mono text-[10px] uppercase tracking-widest text-[#a78bfa]">// CANONICAL SD PROMPT</span>
                            {clipResult?.elapsed_s && (
                              <span className="font-mono text-[9px] text-[#3f3f46]">{clipResult.elapsed_s}s · {clipResult.device}</span>
                            )}
                          </div>
                          <div className="p-4">
                            {!clipResult && (
                              <div className="font-mono text-[11px] text-[#71717a] flex items-center gap-2">
                                <Circle size={12} className="animate-spin" /> Generando prompt canónico…
                              </div>
                            )}
                            {clipResult?.error && (
                              <div className="font-mono text-[11px] text-[#ef4444]/80 leading-relaxed">
                                Prompt canónico no disponible: {clipResult.error}<br/>
                                <span className="text-[#52525b]">Modelo aún cargando (~10-20 min en primer arranque) o microservicio caído.</span>
                              </div>
                            )}
                            {clipResult?.prompt_best && (
                              <>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-[#a78bfa] mb-1.5">BEST · canonical SD/Flux/DALL-E</div>
                                <div className="font-mono text-[12px] text-[#e4e4e7] leading-relaxed mb-3 select-text">{clipResult.prompt_best}</div>
                                <button
                                  onClick={() => navigator.clipboard.writeText(clipResult.prompt_best)}
                                  className="font-mono text-[10px] px-2.5 py-1 border border-[#27272a] rounded text-[#a1a1aa] hover:border-[#a78bfa] hover:text-[#a78bfa] transition-colors mb-4"
                                >COPY BEST</button>
                              </>
                            )}
                            {clipResult?.prompt_fast && (
                              <>
                                <div className="font-mono text-[10px] uppercase tracking-widest text-[#a78bfa]/60 mb-1.5 mt-2">FAST · short tag list</div>
                                <div className="font-mono text-[11px] text-[#a1a1aa] leading-relaxed mb-2 select-text">{clipResult.prompt_fast}</div>
                                <button
                                  onClick={() => navigator.clipboard.writeText(clipResult.prompt_fast)}
                                  className="font-mono text-[10px] px-2.5 py-1 border border-[#27272a] rounded text-[#a1a1aa] hover:border-[#a78bfa] hover:text-[#a78bfa] transition-colors"
                                >COPY FAST</button>
                              </>
                            )}
                            <div className="mt-4 pt-3 border-t border-[#1c1c1e] font-mono text-[10px] text-[#52525b] leading-relaxed">
                              Para Midjourney añade <code className="text-[#71717a]">--ar 16:9 --style raw</code> manualmente.<br/>
                              Para SD/Flux usa el <span className="text-[#a78bfa]/80">BEST</span> tal cual.<br/>
                              Texto literal e iconos del original NO se preservan.
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {recreateState === "running" && (
                    <div className="h-full flex flex-col items-center justify-center p-6 gap-3">
                      <Circle size={30} className="animate-spin text-[#a78bfa]" />
                      <div className="font-mono text-[12px] text-[#a78bfa]">
                        // AGENT GENERATING UI — qwen2.5-coder:7b
                      </div>
                      <div className="font-mono text-[11px] text-[#52525b]">
                        Corre en CPU local, sin GPU: 1-2 minutos. No está colgado.
                      </div>
                    </div>
                  )}

                  {recreateState === "error" && (
                    <div className="h-full flex flex-col items-center justify-center p-6">
                      <div className="border border-[#ef4444]/40 bg-[#ef4444]/5 rounded-lg p-5 max-w-md w-full">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle size={15} className="text-[#ef4444]" />
                          <span className="font-mono text-[12px] text-[#ef4444] uppercase tracking-widest">RECREATE FAILED</span>
                        </div>
                        <p className="font-mono text-[12px] text-[#a1a1aa] leading-relaxed">{recreateError}</p>
                        <button onClick={() => setRecreateState("idle")} className="mt-4 font-mono text-[12px] text-[#ef4444] border border-[#ef4444]/40 rounded hover:bg-[#ef4444]/10 px-3 py-1.5 transition-colors">BACK TO PROMPT</button>
                      </div>
                    </div>
                  )}

                  {recreateState === "done" && recreateHtml && (
                    <div className="h-full flex flex-col">
                      <div className="flex items-center justify-between px-4 py-2 border-b border-[#1c1c1e] flex-shrink-0 bg-[#0a0a0a]">
                        <div className="flex items-center gap-2">
                          <Zap size={12} className="text-[#a78bfa]" />
                          <span className="font-mono text-[11px] text-[#a78bfa] uppercase tracking-widest">recreate.output</span>
                          <span className="font-mono text-[10px] text-[#3f3f46]">
                            {(recreateHtml.length/1024).toFixed(1)}KB · qwen2.5-coder:7b
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => setRecreateView("preview")}
                            className={`font-mono text-[11px] px-2 py-1 border rounded transition-colors ${recreateView === "preview" ? "border-[#a78bfa] text-[#a78bfa] bg-[#a78bfa]/10" : "border-[#27272a] text-[#71717a]"}`}>
                            PREVIEW
                          </button>
                          <button onClick={() => setRecreateView("code")}
                            className={`font-mono text-[11px] px-2 py-1 border rounded transition-colors ${recreateView === "code" ? "border-[#a78bfa] text-[#a78bfa] bg-[#a78bfa]/10" : "border-[#27272a] text-[#71717a]"}`}>
                            CODE
                          </button>
                          <button onClick={() => { navigator.clipboard.writeText(recreateHtml); }}
                            className="font-mono text-[11px] px-2 py-1 border border-[#27272a] rounded text-[#71717a] hover:text-[#a1a1aa]">
                            COPY HTML
                          </button>
                          <button onClick={() => setRecreateState("idle")}
                            className="font-mono text-[11px] px-2 py-1 border border-[#27272a] rounded text-[#71717a] hover:text-[#a1a1aa]">
                            ← PROMPT
                          </button>
                        </div>
                      </div>
                      <div className="flex-1 overflow-auto flex items-center justify-center bg-[#0a0a0a]">
                        {recreateView === "preview" ? (
                          <iframe srcDoc={recreateHtml}
                            sandbox="allow-scripts"
                            title="Recreated UI"
                            style={{ width: "100%", height: "100%", minHeight: "60vh", border: "none", background: "white" }} />
                        ) : (
                          <SyntaxHighlighter language="html" style={atomOneDark} showLineNumbers={false} wrapLongLines={true}
                            customStyle={{ background: "#000000", padding: "1.5rem", margin: 0, fontSize: "0.82rem", lineHeight: "1.7", fontFamily: "'JetBrains Mono','Fira Code',monospace" }}>
                            {recreateHtml}
                          </SyntaxHighlighter>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
